import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight, Plus, Minus, Paperclip, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { submitLead } from "@/lib/submitLead";
import { pushEvent } from "@/lib/analytics";
import { toast } from "sonner";

const serviceOptions = [
  "Security",
  "Project Management",
  "Professional Time-Lapse",
  "License Plate Recognition",
  "PPE Detection",
];

const durationOptions = [
  "Less than 3 months",
  "3–6 months",
  "6–12 months",
  "12+ months",
];

const australianStates = [
  "NSW",
  "VIC",
  "QLD",
  "WA",
  "SA",
  "TAS",
  "ACT",
  "NT",
];

const inputClasses =
  "w-full rounded-lg bg-secondary border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary";

function QuantitySelector({ value, onChange, label }: { value: number; onChange: (v: number) => void; label: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-secondary border border-border px-4 py-3">
      <span className="text-sm text-foreground">{label}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          onClick={() => onChange(Math.max(0, value - 1))}
          className="h-7 w-7 rounded-md bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors text-foreground"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <span className="text-sm font-medium text-foreground w-6 text-center">{value}</span>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          onClick={() => onChange(value + 1)}
          className="h-7 w-7 rounded-md bg-primary flex items-center justify-center hover:bg-primary/90 transition-colors text-primary-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function QuoteForm({ title }: { title?: string }) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    company_name: "",
    email: "",
    phone: "",
    project_name: "",
    site_location: "",
    site_state: "",
    project_duration: "",
    single_towers_qty: 0,
    double_towers_qty: 0,
    services_needed: [] as string[],
    project_details: "",
  });
  const [attachment, setAttachment] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleService = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      services_needed: prev.services_needed.includes(service)
        ? prev.services_needed.filter((s) => s !== service)
        : [...prev.services_needed, service],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let attachmentUrl: string | null = null;

      if (attachment) {
        const fileExt = attachment.name.split(".").pop();
        const filePath = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from("form-attachments")
          .upload(filePath, attachment);
        if (uploadError) throw uploadError;
        // Bucket is private — store the path so the backend can generate a signed URL on demand.
        attachmentUrl = `form-attachments/${filePath}`;
      }

      const { error } = await supabase.functions.invoke("submit-quote", {
        body: {
          full_name: formData.full_name,
          company_name: formData.company_name || null,
          email: formData.email,
          phone: formData.phone,
          project_name: formData.project_name || null,
          site_location: formData.site_location || null,
          site_state: formData.site_state || null,
          project_duration: formData.project_duration || null,
          single_towers_qty: formData.single_towers_qty,
          double_towers_qty: formData.double_towers_qty,
          services_needed: formData.services_needed.length ? formData.services_needed : null,
          project_details: formData.project_details || null,
          attachment_url: attachmentUrl,
        },
      });

      if (error) throw error;

      // Send lead to CRM
      try {
        await submitLead({
          company_name: formData.company_name || formData.full_name,
          contact_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          project_name: formData.project_name || "",
          project_address: formData.site_location ? `${formData.site_location}, ${formData.site_state}` : "",
          source: "Website",
          estimated_start_date: "",
          estimated_duration_months: formData.project_duration ? parseInt(formData.project_duration) || 0 : 0,
          single_vision_towers: formData.single_towers_qty,
          double_vision_towers: formData.double_towers_qty,
          service_security: formData.services_needed.includes("Security"),
          service_project_management: formData.services_needed.includes("Project Management"),
          service_time_lapse: formData.services_needed.includes("Professional Time-Lapse"),
          service_lpr: formData.services_needed.includes("License Plate Recognition"),
          service_ppe_detection: formData.services_needed.includes("PPE Detection"),
          details: formData.project_details || "",
          files: attachment ? [attachment] : [],
        });
        console.log("CRM lead created successfully");
      } catch (crmError) {
        console.error("CRM lead creation failed:", crmError);
      }
      pushEvent({
        event: "quote_request_submit",
        form_location: typeof window !== "undefined" ? window.location.pathname : "",
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Quote submission error:", err);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="glass-card rounded-xl p-8 text-center">
        <div className="h-16 w-16 rounded-full gradient-orange mx-auto mb-4 flex items-center justify-center text-2xl text-primary-foreground">
          ✓
        </div>
        <h3 className="font-display text-2xl font-bold text-foreground mb-2">Thank You!</h3>
        <p className="text-muted-foreground">
          We'll be in touch within 24 hours with your custom quote.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {title !== undefined ? (
        <h3 className="font-display text-2xl font-bold text-foreground mb-6">{title}</h3>
      ) : (
        <div className="text-center mb-10">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-3">
            Get a Custom <span className="text-primary">Quote</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Tell us about your next project. Our specialists will design an intelligent site
            monitoring and security plan tailored exactly to your needs.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="glass-card rounded-xl p-8 space-y-8">
        {/* Your Details */}
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground mb-1">Your Details</h3>
          <div className="border-b border-border mb-5" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Full Name</label>
              <input type="text" name="full_name" value={formData.full_name} onChange={handleChange} placeholder="John Doe" required className={inputClasses} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Company Name</label>
              <input type="text" name="company_name" value={formData.company_name} onChange={handleChange} placeholder="Acme Construction" className={inputClasses} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" required className={inputClasses} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Phone Number</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="0400 000 000" required pattern="\d{10}" maxLength={10} title="Please enter a 10-digit phone number" className={inputClasses} />
            </div>
          </div>
        </div>

        {/* Project Requirements */}
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground mb-1">Project Requirements</h3>
          <div className="border-b border-border mb-5" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Project Name</label>
              <input type="text" name="project_name" value={formData.project_name} onChange={handleChange} placeholder="e.g. Westfield Tower Build" required className={inputClasses} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Site Location / Suburb</label>
              <input type="text" name="site_location" value={formData.site_location} onChange={handleChange} placeholder="e.g. Parramatta" required className={inputClasses} />
            </div>
            <div>
              <label htmlFor="site_state" className="block text-sm font-medium text-foreground mb-1.5">State</label>
              <select id="site_state" name="site_state" value={formData.site_state} onChange={handleChange} required className={inputClasses}>
                <option value="">Select a state...</option>
                {australianStates.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="project_duration" className="block text-sm font-medium text-foreground mb-1.5">Estimated Project Duration</label>
              <select id="project_duration" name="project_duration" value={formData.project_duration} onChange={handleChange} className={inputClasses}>
                <option value="">Select an estimated timeframe...</option>
                {durationOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Hardware */}
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground mb-1">Hardware</h3>
          <div className="border-b border-border mb-5" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <QuantitySelector
              label="Single Vision Towers"
              value={formData.single_towers_qty}
              onChange={(v) => setFormData((prev) => ({ ...prev, single_towers_qty: v }))}
            />
            <QuantitySelector
              label="Double Vision Towers"
              value={formData.double_towers_qty}
              onChange={(v) => setFormData((prev) => ({ ...prev, double_towers_qty: v }))}
            />
          </div>
        </div>

        {/* Services */}
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground mb-1">Services</h3>
          <div className="border-b border-border mb-5" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {serviceOptions.map((service) => (
              <label
                key={service}
                className="flex items-center gap-3 rounded-lg bg-secondary border border-border px-4 py-3 cursor-pointer hover:border-primary/50 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={formData.services_needed.includes(service)}
                  onChange={() => toggleService(service)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-sm text-foreground">{service}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Additional Details */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Additional Details or Specific Requirements
          </label>
          <textarea
            name="project_details"
            value={formData.project_details}
            onChange={handleChange}
            placeholder="Tell us about the site layout, access issues, or specific security concerns..."
            rows={4}
            className={`${inputClasses} resize-vertical`}
          />
        </div>

        {/* File Attachment */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Attach a File <span className="text-muted-foreground font-normal">(optional — site plans, drawings, etc.)</span>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && file.size > 10 * 1024 * 1024) {
                toast.error("File must be under 10 MB.");
                return;
              }
              setAttachment(file || null);
            }}
          />
          {attachment ? (
            <div className="flex items-center gap-3 rounded-lg bg-secondary border border-border px-4 py-3">
              <Paperclip className="h-4 w-4 text-primary shrink-0" />
              <span className="text-sm text-foreground truncate flex-1">{attachment.name}</span>
              <button
                type="button"
                onClick={() => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Remove attachment"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 rounded-lg bg-secondary border border-dashed border-border px-4 py-3 text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors w-full"
            >
              <Paperclip className="h-4 w-4" />
              Click to attach a file (PDF, images, docs — max 10 MB)
            </button>
          )}
        </div>

        {/* Submit */}
        <div>
          <div className="border-b border-border mb-6" />
          <Button type="submit" variant="hero" size="xl" className="w-full sm:w-auto" disabled={loading}>
            {loading ? "Submitting..." : "Request Custom Quote"}
            {!loading && <ArrowRight className="h-5 w-5 ml-1" />}
          </Button>
        </div>
      </form>
    </div>
  );
}
